/**
 * Suburb Lists API
 *
 * CRUD operations for canonical, reusable suburb sets.
 */

import type { SuburbList, SuburbListItem } from "@repo/types/suburb-list";
import { supabase } from "@/integrations/supabase/client";

/** PostgREST default max rows per request — paginate or counts truncate incorrectly. */
const LIST_ITEM_COUNT_PAGE_SIZE = 1000;

async function countSuburbListItemsByListId(listIds: string[]): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  if (listIds.length === 0) return counts;

  let offset = 0;
  for (;;) {
    const { data: page, error } = await supabase
      .from("suburb_list_items")
      .select("list_id")
      .in("list_id", listIds)
      .order("list_id", { ascending: true })
      .range(offset, offset + LIST_ITEM_COUNT_PAGE_SIZE - 1);

    if (error) throw error;
    const rows = page ?? [];
    if (rows.length === 0) break;
    for (const row of rows) {
      counts[row.list_id] = (counts[row.list_id] ?? 0) + 1;
    }
    if (rows.length < LIST_ITEM_COUNT_PAGE_SIZE) break;
    offset += LIST_ITEM_COUNT_PAGE_SIZE;
  }

  return counts;
}

/**
 * Fetch all suburb lists with item counts.
 * Uses a manual count since Supabase doesn't expose aggregate joins cleanly;
 * counts are paginated so lists beyond the default row limit still sum correctly.
 */
export async function fetchSuburbLists(): Promise<SuburbList[]> {
  const { data, error } = await supabase
    .from("suburb_lists")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw error;
  if (!data || data.length === 0) return [];

  const listIds = data.map((l) => l.id);
  const counts = await countSuburbListItemsByListId(listIds);

  return data.map((list) => ({
    ...list,
    item_count: counts[list.id] ?? 0,
  }));
}

/**
 * Fetch all items in a suburb list, with joined suburb data.
 */
export async function fetchSuburbListItems(listId: string): Promise<SuburbListItem[]> {
  const { data, error } = await supabase
    .from("suburb_list_items")
    .select("*, suburbs(*)")
    .eq("list_id", listId)
    .order("created_at", { ascending: true });

  if (error) throw error;
  return (data ?? []) as SuburbListItem[];
}

/**
 * Distinct AU states covered by all suburbs in the given lists (for adapter compatibility).
 */
export async function fetchDistinctStatesForSuburbListIds(listIds: string[]): Promise<string[]> {
  if (listIds.length === 0) return [];

  const { data, error } = await supabase
    .from("suburb_list_items")
    .select("suburbs(state)")
    .in("list_id", listIds);

  if (error) throw error;

  const states = new Set<string>();
  for (const row of data ?? []) {
    const suburb = row.suburbs as { state?: string } | null;
    const st = suburb?.state;
    if (st) states.add(st);
  }
  return [...states];
}

/**
 * Create a new suburb list, optionally with initial suburb IDs.
 */
export async function createSuburbList(
  name: string,
  description?: string,
  suburbIds?: string[],
): Promise<SuburbList> {
  const { data, error } = await supabase
    .from("suburb_lists")
    .insert({ name, description: description ?? null })
    .select()
    .single();

  if (error) throw error;

  if (suburbIds && suburbIds.length > 0) {
    const items = suburbIds.map((suburb_id) => ({
      list_id: data.id,
      suburb_id,
    }));
    const { error: itemsError } = await supabase.from("suburb_list_items").insert(items);

    if (itemsError) throw itemsError;
  }

  return { ...data, item_count: suburbIds?.length ?? 0 };
}

/**
 * Update a suburb list's name and/or description.
 */
export async function updateSuburbList(
  listId: string,
  updates: { name?: string; description?: string },
): Promise<SuburbList> {
  const { data, error } = await supabase
    .from("suburb_lists")
    .update(updates)
    .eq("id", listId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete a suburb list (items cascade-delete).
 */
export async function deleteSuburbList(listId: string): Promise<void> {
  const { error } = await supabase.from("suburb_lists").delete().eq("id", listId);

  if (error) throw error;
}

/**
 * Add suburbs to an existing list.
 */
export async function addSuburbsToList(listId: string, suburbIds: string[]): Promise<void> {
  if (suburbIds.length === 0) return;

  const items = suburbIds.map((suburb_id) => ({
    list_id: listId,
    suburb_id,
  }));

  const { error } = await supabase
    .from("suburb_list_items")
    .upsert(items, { onConflict: "list_id,suburb_id" });

  if (error) throw error;
}

/**
 * Remove a single suburb from a list.
 */
export async function removeSuburbFromList(listId: string, suburbId: string): Promise<void> {
  const { error } = await supabase
    .from("suburb_list_items")
    .delete()
    .eq("list_id", listId)
    .eq("suburb_id", suburbId);

  if (error) throw error;
}
